import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { CHANGE_FILTER, CLEAR_FILTERS } from '../../constants/filters';
import { CHANGE_SEARCH_PARAMS } from '../../constants/search';
import { CHANGE_SORTING } from '../../constants/sorting';
import { PAGINATION } from './constants';

import type {
  HotelsState,
  SetCurrentVisibleHotels,
  SetHasPaginationAction,
  SetHotelsType,
  SetVisibleHotelsAction,
  UpdatePaginationAction,
} from './types';

const initialState: HotelsState = {
  list: [],
  hotelsCount: 0,
  prevHotelsCount: null,
  hasPagination: null,
  currentVisibleHotels: [],
  currentVisiblePenalizedHotels: [],
  visibleLimit: PAGINATION.VISIBLE_LIMIT,
  pageNumber: PAGINATION.PAGE_NUMBER,
  areHotelsVisible: false,
  arePenalizedHotelsVisible: false,
  topHotelName: null,
  isTopHotelNotFound: false,
};

// функция установки нужного visibleLimit для показа скелетонов и исключения прыгающей страницы при смене фильтров
const setVisibleLimit = (state) => {
  // Используем константу для фиксированного значения лимита при сбросе фильтров
  const DEFAULT_VISIBLE_LIMIT = 10;
  const currentVisibleLimit = state.visibleLimit;
  if (state.hotelsCount <= currentVisibleLimit) return state.hotelsCount;
  if (
    state.hotelsCount > DEFAULT_VISIBLE_LIMIT &&
    currentVisibleLimit < DEFAULT_VISIBLE_LIMIT
  )
    return DEFAULT_VISIBLE_LIMIT;
  return currentVisibleLimit;
};

/**
 * Сбрасываем сохраненные отели при изменении фильтра или сортировки
 * Запоминаем значение hasPagination, currentVisibleHotels и visibleLimit
 */
const resetHotels = (state: HotelsState, action) => {
  if (action.payload?.path === 'hotel_name') return state;
  return {
    ...initialState,
    currentVisibleHotels: state.currentVisibleHotels,
    hasPagination: state.hasPagination,
    visibleLimit: state.visibleLimit,
    prevHotelsCount: state.hotelsCount,
    topHotelName: state.topHotelName,
    isTopHotelNotFound: state.isTopHotelNotFound,
  };
};

const hotelsSlice = createSlice({
  name: 'hotels',
  initialState,
  reducers: {
    setHotels: (state, action: SetHotelsType) => {
      const { hotels, hotelsCount, needMergeHotels } = action.payload;

      state.list = needMergeHotels ? [...state.list, ...hotels] : hotels;
      state.hotelsCount = hotelsCount;

      state.visibleLimit = setVisibleLimit(state);
      if (!state.arePenalizedHotelsVisible) {
        state.currentVisiblePenalizedHotels = [];
      }
    },
    setVisibleHotels: (state, action: SetVisibleHotelsAction) => ({
      ...state,
      visibleLimit: action.payload,
    }),
    setPreviousHotelsCount: (state, action) => {
      state.prevHotelsCount = action.payload;
    },
    updatePagination: (state, action: UpdatePaginationAction) => {
      state.pageNumber = action.payload;
    },
    setHasPagination: (state, action: SetHasPaginationAction) => {
      state.hasPagination = action.payload;
    },
    setHotelsVisibility: (state, action) => {
      state.areHotelsVisible = action.payload;
    },
    setPenalizedHotelsVisibility: (state, action) => {
      state.arePenalizedHotelsVisible = action.payload;
    },
    setCurrentVisibleHotels: (state, action: SetCurrentVisibleHotels) => {
      state.currentVisibleHotels = [...action.payload];
      if (!action.payload.length) return;
      const maxVisibleHotelIndex = Math.max(...state.currentVisibleHotels);
      state.visibleLimit =
        state.visibleLimit >= maxVisibleHotelIndex
          ? state.visibleLimit
          : maxVisibleHotelIndex;
    },
    setCurrentVisiblePenalizedHotels: (
      state,
      action: SetCurrentVisibleHotels,
    ) => {
      if (!action.payload.length) return;
      state.currentVisiblePenalizedHotels = [...action.payload];
    },
    setTopHotelData: (
      state,
      action: PayloadAction<{
        topHotelName: string | null;
        isTopHotelNotFound: boolean;
      }>,
    ) => {
      state.topHotelName = action.payload.topHotelName;
      state.isTopHotelNotFound = action.payload.isTopHotelNotFound;
    },
  },
  extraReducers: {
    [CHANGE_FILTER]: resetHotels,
    [CLEAR_FILTERS]: resetHotels,
    [CHANGE_SEARCH_PARAMS]: resetHotels,
    [CHANGE_SORTING]: resetHotels,
  },
});

export const {
  setHotels,
  setVisibleHotels,
  setPreviousHotelsCount,
  updatePagination,
  setHasPagination,
  setCurrentVisibleHotels,
  setCurrentVisiblePenalizedHotels,
  setHotelsVisibility,
  setPenalizedHotelsVisibility,
  setTopHotelData,
} = hotelsSlice.actions;

export const { name } = hotelsSlice;

export default hotelsSlice.reducer;
